import type { BoxProps } from '@withjoy/joykit';
import type { HeaderBackgroundEffect } from '@shared/types/WebsiteHeaderSettings.types';
import chroma from 'chroma-js';

/**
 * Maps a {@link HeaderBackgroundEffect} value to Joykit `BoxProps` that render
 * the corresponding visual treatment (glass blur, semi-transparent fill, etc.).
 *
 * Shared between the guest site header and the admin template thumbnail so both
 * render identical effects for the same settings.
 *
 * Uses a `switch` (rather than building a `Record<HeaderBackgroundEffect, BoxProps>`
 * up front) so each arm only runs when selected. The previous record pattern
 * eagerly evaluated every arm — meaning a bad `backgroundColor` passed to
 * `getBackgroundEffectStyles('clear', '...')` would still throw through the
 * `transparent` arm's `chroma(...)` call, crashing the page on a discarded
 * value. Observed in production when Stage 2 emitted
 * `headerTitleStyle.backgroundColor: "transparent"` on a `clear` effect.
 */
export const getBackgroundEffectStyles = (effect: HeaderBackgroundEffect | undefined, backgroundColor?: string): BoxProps => {
  if (!effect) return {};

  switch (effect) {
    case 'clear':
      return { backgroundColor: 'transparent' };
    case 'glass':
      return {
        backgroundColor: 'rgba(255, 255, 255, 0.29)',
        borderRadius: '16px',
        boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
        backdropFilter: 'blur(5.6px)',
        border: '1px solid rgba(255, 255, 255, 0.26)',
      };
    case 'transparent':
      // Defensive: chroma throws on unparseable colors (e.g., the literal CSS
      // keyword "transparent"). Fall back to white-with-alpha rather than
      // letting the throw propagate up to the renderer.
      try {
        return {
          backgroundColor: chroma(backgroundColor || '#ffffff')
            .alpha(0.1)
            .hex(),
        };
      } catch {
        return { backgroundColor: chroma('#ffffff').alpha(0.1).hex() };
      }
    case 'solid':
      return { backgroundColor };
  }
};
